import { ConfigurationsResponse } from "../../models/ConfigurationsResponse";
import { HttpRequestWrapper } from "../HttpRequestWrapper";
import { KeyValuePair } from "../../models/KeyValuePair";
import { NewformaApiClient } from "./NewformaApiClient";
import { Logger, Log } from "../Logger";
import { UnauthenticatedNewformaApiClient } from "../UnauthenticatedNewformaApiClient";
import { TeachingBubblesTypes } from "../../models/TeachingBubblesTypes";

export class ConfigurationsApiService {
    private readonly configurationsUri = "/v1/configurations";

    private configurations: KeyValuePair[] = [];
    private teachingBubbles: string[] = Object.keys(TeachingBubblesTypes).filter((item) => isNaN(Number(item)));

    constructor(
        private newformaApiClient: NewformaApiClient,
        private unauthenticatedClient: UnauthenticatedNewformaApiClient,
        private requestWrapper: HttpRequestWrapper,
        private logger: Logger
    ) {}

    async getUnauthenticatedConfigurations(): Promise<void> {
        Log.info("Retrieving unauthenticated configurations");
        const configurationsResponse = await this.unauthenticatedClient.getConfigurations();
        this.configurations = configurationsResponse.configurations;
        Log.info("unauthenticated configurations");
    }

    async getAuthenticatedConfigurations(): Promise<void> {
        this.logger.info("Retrieving authenticated configurations");

        const configurationsResponse = await this.makeGetAuthenticatedConfigurationsRequest();

        configurationsResponse.configurations.concat(configurationsResponse.license.parameters).forEach((pair: any) => {
            const existingItem = this.configurations.find((x) => x.key === pair.key);
            if (existingItem) {
                existingItem.value = pair.value;
            } else {
                this.configurations.push(pair);
            }
        });

        Log.table("AuthenticatedConfigurations", this.configurations);
        this.teachingBubbles = configurationsResponse.teachingBubbles;
        Log.info(`teaching bubbles ${this.teachingBubbles}`);
    }

    async updateCompletedTeachingBubblesSteps(teachingBubblesToUpdate: string[]): Promise<void> {
        this.logger.info("Updating teaching bubbles progress");
        const url = `${this.newformaApiClient.getHostNameWithProtocol()}${this.configurationsUri}/me/teachingBubbles`;
        const payload = JSON.stringify({ bubbleNames: teachingBubblesToUpdate });
        const options = {
            url: url,
            method: "PATCH",
            body: payload,
            headers: { "x-newforma-agent": this.newformaApiClient.getNewformaAgent() },
        };

        return this.newformaApiClient.makeRequest(options, async (signedOptions) =>
            this.requestWrapper.patch(url, undefined, signedOptions.headers, payload)
        );
    }

    updateCompletedTeachingBubblesCollection(teachingBubble: TeachingBubblesTypes): void {
        this.teachingBubbles.push(teachingBubble);
    }

    private async makeGetAuthenticatedConfigurationsRequest(): Promise<ConfigurationsResponse> {
        const url = `${this.newformaApiClient.getHostNameWithProtocol()}${this.configurationsUri}/me`;
        const options = {
            url: url,
            method: "GET",
            headers: { "x-newforma-agent": this.newformaApiClient.getNewformaAgent() },
        };

        return this.newformaApiClient.makeRequest(options, async (signedOptions) =>
            this.requestWrapper.get(url, undefined, signedOptions.headers, undefined)
        );
    }

    isActionItemsSupported(): boolean {
        return this.isFeatureSupported("hasActionItemsSupport");
    }

    isSubmittalsSupported(): boolean {
        return this.isFeatureSupported("hasSubmittalsSupport");
    }

    isRfiSupported(): boolean {
        return this.isFeatureSupported("hasRfisSupport");
    }

    isRfiRespondCloseSupported(): boolean {
        return this.isFeatureSupported("respond-close-file-as-rfi-response");
    }

    isRfiRespondAndCloseSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasRespondCloseForRFI");
    }

    isEulaSupported(): boolean {
        return this.isFeatureSupported("eula-is-enabled");
    }

    isFileTransferAndEditorSupported(): boolean {
        return this.isFeatureSupported("file-transfer-add-in");
    }

    isSupportedByNL1221Version(): boolean {
        return this.isFeatureSupported("hasFileTransfersSupport");
    }

    isFilePathSupportedInAttachments(): boolean {
        return this.isFeatureSupported("canAttachFilesInFolders");
    }

    isForwardSubmittalSupported(): boolean {
        return this.isFeatureSupported("outlook-add-in-forward-a-submittal-for-review");
    }

    isForwardRfiSupported(): boolean {
        return this.isFeatureSupported("outlook-add-in-forward-an-rfi-for-review");
    }

    isForwardSubmittalAndRfiSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasForwardSubmittalAndRfiSupport");
    }
    // group together for SubmittalRespondAndClose
    isSubmittalRespondCloseSupported(): boolean {
        return this.isFeatureSupported("respond-close-file-as-submittal-response");
    }

    isSubmittalRespondAndCloseSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasRespondCloseForSubmittal");
    }
    // end of group

    isFileOnSendGlobalProjectsSupported(): boolean {
        return this.isFeatureSupported("new-file-on-send-global-projects");
    }
    isFileOnSendGlobalProjectsSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasFileOnSendForGlobalProjectSupport");
    }
    isSignInFieldOnLoginPage(): boolean {
        return this.isFeatureSupported("sign-in-field-on-login-page");
    }

    isFileIntoAnItemSupported(): boolean {
        return this.isFeatureSupported("file-into-an-item");
    }

    isProjectItemSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasProjectItemSupport");
    }

    isProjectItemPendingExpectedSupported(): boolean {
        return this.isFeatureSupported("hasProjectItemPendingExpectedSupport");
    }

    isFileEmailIntoSubfolderSupported(): boolean {
        return this.isFeatureSupported("file-email-into-subfolder");
    }

    isSendFilesFromProjectSupported(): boolean {
        return (
            this.isFeatureSupported("send-files-from-project") &&
            this.isFeatureSupported("hasSendFilesFromProjectSupport")
        );
    }

    isGlobalProjectSearchSupported(): boolean {
        return (
            this.isFeatureSupported("outlook-addin-global-project-search") &&
            this.isFeatureSupported("hasGlobalProjectSearchSupport")
        );
    }

    isProjectSubfolderSupportedBasedOnNLVersion(): boolean {
        return this.isFeatureSupported("hasProjectSubfolderSupport");
    }

    isFileRfiFluent9Supported(): boolean {
        return this.isFeatureSupported("file-rfi-fui9");
    }

    shouldTeachingBubbleBeShown(bubbleName: TeachingBubblesTypes): boolean {
        let bubbleResult: boolean = false;
        try {
            if (Array.isArray(this.teachingBubbles)) {
                bubbleResult = !this.teachingBubbles.some((teachingBubble) => teachingBubble === bubbleName);
            }
        } catch {
            Log.info(`Error: Unable to obtain teaching bubble.`);
        }
        Log.info(`shouldTeachingBubbleBeShown ${bubbleName} = ${bubbleResult}`);
        return bubbleResult;
    }

    private isFeatureSupported(key: string): boolean {
        const result = this.configurations?.find((x) => x.key === key);
        if (!result) {
            return false;
        }
        return result.value;
    }
}
